import { useEffect, useRef } from 'react'

import { copyPostMessageTypes } from '@/configuration/pages/ConfigurationPage/ReturnsPortalConfiguration/ReturnsPortalCopiesPage/constants'

const buildOverrides = (customCopies, lang, isCustomCopyActive) => {
  const langCopies = customCopies?.[lang] || {}
  const overrides = {}

  Object.entries(langCopies).forEach(([key, value]) => {
    overrides[key] = isCustomCopyActive?.(key) ? value : null
  })

  return overrides
}

const useCopyPreviewMessaging = ({ previewUrl, customCopies, lang, isCustomCopyActive }) => {
  const iframeRef = useRef(null)
  const iframeReady = useRef(false)
  const lastPayloadRef = useRef(null)
  const functionsRef = useRef({})

  const getOrigin = () => {
    if (!previewUrl) return null

    try {
      return new URL(previewUrl).origin
    } catch {
      return null
    }
  }

  const sendCopiesUpdate = () => {
    const iframe = iframeRef.current
    const origin = getOrigin()

    if (!iframe?.contentWindow || !origin) return

    const payload = { lang, overrides: buildOverrides(customCopies, lang, isCustomCopyActive) }
    iframe.contentWindow.postMessage({ type: copyPostMessageTypes.copiesUpdate, payload }, origin)
  }

  functionsRef.current = { getOrigin, sendCopiesUpdate }

  useEffect(() => {
    const handleMessage = event => {
      const expectedOrigin = functionsRef.current.getOrigin()

      if (expectedOrigin && event.origin !== expectedOrigin) return

      const { type } = event.data || {}

      if (type === copyPostMessageTypes.iframeReady) {
        iframeReady.current = true
        functionsRef.current.sendCopiesUpdate()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    iframeReady.current = false
  }, [previewUrl])

  useEffect(() => {
    if (!iframeReady.current) return

    const payload = JSON.stringify({ lang, overrides: buildOverrides(customCopies, lang, isCustomCopyActive) })

    if (lastPayloadRef.current === payload) return

    lastPayloadRef.current = payload

    functionsRef.current.sendCopiesUpdate()
  }, [customCopies, lang, isCustomCopyActive])

  return { iframeRef }
}

export default useCopyPreviewMessaging
